

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from 'auth/Auth';

const CustomFieldsSection = forwardRef(({ objectType, objectData, isEditing, onFieldUpdate, onError, formErrors }, ref) => {
  const { axiosInstance } = useAuth();
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [localValues, setLocalValues] = useState({}); // Always initialize as empty object
  const [isDirty, setIsDirty] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    fetchCustomFields();
  }, [objectType]);

  useEffect(() => {
    if (objectData && customFields.length > 0) {
      const initialValues = {};
      customFields.forEach(field => {
        let value = objectData[field.column_name] ?? localValues[field.column_name] ?? '';
        if (field.type.toLowerCase() === 'boolean') {
          value = value === true || value === 'true' ? 'Yes' : value === false || value === 'false' ? 'No' : value;
        } else if (field.type.toLowerCase() === 'int' && value !== null && value !== undefined) {
          value = String(value);
        }
        initialValues[field.column_name] = value;
      });
      console.log('Initializing localValues:', initialValues);
      setLocalValues(prev => ({ ...prev, ...initialValues }));
      setIsInitialized(true);
      setIsDirty({});
    }
  }, [objectData, customFields]);

  const fetchCustomFields = async () => {
    try {
      const response = await axiosInstance.get(`/custom-fields/${objectType}`);
      setCustomFields(response.data);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field, value) => {
    console.log(`Validating field: ${field.column_name}, Value: ${value}, Required: ${field.is_required}`);
    let error = '';
    if (field.is_required && (value === '' || value === null || value === undefined)) {
      error = 'This field is required';
    } else if (value !== '' && value !== null && value !== undefined) {
      if (field.type.toLowerCase() === 'int') {
        const numValue = Number(value);
        if (isNaN(numValue) || (typeof value === 'string' && value.trim() === '')) {
          error = 'Please enter a valid number';
        } else if (value.toString().split('.')[1]?.length > 2) {
          error = 'Please enter a number with up to 2 decimal places';
        }
      } else if (field.type.toLowerCase() === 'boolean') {
        if (value !== 'Yes' && value !== 'No') {
          error = 'Please select Yes or No';
        }
      } else if (field.type.toLowerCase() === 'string' && value.length > 255) {
        error = 'Text cannot exceed 255 characters';
      }
    }
    console.log(`Validation error for ${field.column_name}: ${error}`);
    return error;
  };

  const formatValue = (field, value) => {
    if (value === '' || value === undefined || value === null) {
      return null;
    }
    if (field.type.toLowerCase() === 'int') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        const hasDecimals = value.toString().includes('.');
        const formattedNum = hasDecimals ? numValue.toFixed(2) : numValue.toString();
        return formattedNum;
      }
      return null;
    }
    if (field.type.toLowerCase() === 'boolean') {
      return value === "Yes" ? "true" : "false";
    }
    return String(value);
  };

  const handleFieldChange = (field, value) => {
    console.log(`Field ${field.column_name} changed to:`, value);
    setLocalValues(prev => {
      const newValues = { ...prev, [field.column_name]: value };
      console.log('Updated localValues:', newValues);
      return newValues;
    });
    setIsDirty(prev => ({
      ...prev,
      [field.column_name]: true
    }));
    const error = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      [field.column_name]: error
    }));
    if (onError) {
      onError(field.column_name, error);
    }
    const formattedValue = formatValue(field, value);
    onFieldUpdate(field.column_name, formattedValue);
  };

 useImperativeHandle(ref, () => ({
  validateAllFields: () => {
    console.log('Validating with localValues:', localValues);
    const errors = {};
    let isValid = true;
    customFields.forEach(field => {
      const value = localValues[field.column_name] ?? '';
      const error = validateField(field, value);
      if (error) {
        errors[field.column_name] = error;
        if (onError) {
          onError(field.column_name, error);
        }
        isValid = false;
      }
    });
    console.log('Validation errors:', errors);
    setFieldErrors(prev => ({ ...prev, ...errors }));
    return isValid;
  }
}));

  const renderField = (field) => {
    if (!field || !field.column_name) {
      console.error('Invalid field object:', field);
      return null;
    }

    const value = localValues[field.column_name] ?? '';
    const error = fieldErrors[field.column_name];
    const isDirtyField = isDirty[field.column_name];

    if (isEditing) {
      switch (field.type.toLowerCase()) {
        case 'boolean':
          return (
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel 
                id={`${field.column_name}-label`}
                error={!!error}
                shrink={true}
              >
                {field.ui_label}
              </InputLabel>
              <Select
                labelId={`${field.column_name}-label`}
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                displayEmpty
                error={!!error}
                label={field.ui_label}
                sx={{ 
                  height: '40px',
                  padding: '11px',
                  '& .MuiOutlinedInput-input': { padding: '8px 14px' }
                }}
              >
                {!field.is_required && (
                  <MenuItem value="">
                    <em>Select {field.ui_label}...</em>
                  </MenuItem>
                )}
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {error && (
                <MDTypography variant="caption" color="error" mt={0.5}>
                  {error}
                </MDTypography>
              )}
            </FormControl>
          );
        case 'int':
          return (
            <TextField
              fullWidth
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              required={field.is_required}
              error={!!error}
              helperText={error || ' '}
              size="small"
              placeholder={field.ui_label}
              label={field.ui_label}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
              inputProps={{ step: "1" }}
            />
          );
        default:
          return (
            <TextField
              fullWidth
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              required={field.is_required}
              error={!!error}
              helperText={error || ' '}
              size="small"
              placeholder={field.ui_label}
              label={field.ui_label}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
              inputProps={{ maxLength: 255 }}
            />
          );
      }
    }

    return (
      <MDBox>
        <MDTypography variant="button" fontWeight="regular" color="text">
          <strong>{field.ui_label}:</strong>
        </MDTypography>
        <MDTypography
          component="span"
          variant="button"
          fontWeight="regular"
          color="text"
          ml={1}
        >
          {value || '-'}
        </MDTypography>
      </MDBox>
    );
  };

  if (!loading && customFields.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </MDBox>
    );
  }

  return (
    <MDBox mt={3}>
      <Grid container spacing={3}>
        {customFields.map((field) => (
          <Grid item xs={12} md={6} key={field.id}>
            {renderField(field)}
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
});

export default CustomFieldsSection;